import NewsInlineBanner from '@/components/NewsInlineBanner'
import SiteFooter from '@/components/SiteFooter'
import SiteHeader from '@/components/SiteHeader'
import WatermarkedImage from '@/components/WatermarkedImage'
import { useNews } from '@/hooks/useNews'
import { useNewsBanners } from '@/hooks/useNewsBanners'
import { useRadioData } from '@/hooks/useRadioData'
import { interleaveNewsBanners } from '@/lib/newsBanners'
import { articlePath, formatNewsDate, splitNewsBody } from '@/lib/news'
import { NEWS_PAGE_SIZE } from '@/types/news'
import { useHead } from '@unhead/react'
import {ChevronLeft, ChevronRight} from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

function NewsList() {
  const { settings, social } = useRadioData()
  const { articles, loading } = useNews(true)
  const { banners: newsBanners } = useNewsBanners()
  const [page, setPage] = useState(0)
  const published = useMemo(() => articles.filter((item) => item?.published !== false), [articles])
  const totalPages = Math.max(1, Math.ceil(published.length / NEWS_PAGE_SIZE))
  const currentPage = Math.min(page, totalPages - 1)
  const slice = published.slice(currentPage * NEWS_PAGE_SIZE, currentPage * NEWS_PAGE_SIZE + NEWS_PAGE_SIZE)
  const feed = useMemo(
    () => interleaveNewsBanners(slice.map((item) => item._id || item.slug), newsBanners, 1),
    [slice, newsBanners],
  )
  const articlesByKey = useMemo(() => {
    const map = new Map<string, (typeof slice)[number]>()
    slice.forEach((item) => map.set(item._id || item.slug, item))
    return map
  }, [slice])
  const title = `Notícias — ${settings?.stationName || 'Bonita FM'}`

  useHead({
    title,
    htmlAttrs: { lang: 'pt-BR' },
    meta: [
      { name: 'description', content: `Últimas notícias da ${settings?.stationName || 'Bonita FM'}.` },
      { property: 'og:title', content: title },
      { property: 'og:type', content: 'website' },
    ],
  })

  return (
    <div className="radio-bg min-h-screen">
      <SiteHeader settings={settings} social={social} />
      <main className="mx-auto max-w-5xl px-3 py-10 sm:px-4 sm:py-12 lg:px-6 lg:py-14">
        <p className="text-xs font-extrabold tracking-[0.2em] text-[#ffd21f]">NOTÍCIAS</p>
        <h1 className="mt-2 font-display text-3xl font-black sm:text-4xl">Matérias completas</h1>
        <p className="mt-3 text-sm text-white/75">Até 10 matérias por página. Clique para ler o texto completo.</p>

        {loading ? (
          <div className="mt-10 space-y-8">
            {Array.from({ length: 2 }).map((_, index) => (
              <div key={index} className="overflow-hidden rounded-[28px] bg-white/10">
                <div className="h-48 animate-pulse bg-white/15 sm:h-64" />
                <div className="space-y-3 p-5 sm:p-8">
                  <div className="h-4 w-24 animate-pulse rounded-full bg-white/20" />
                  <div className="h-7 w-4/5 animate-pulse rounded bg-white/20" />
                  <div className="h-4 w-full animate-pulse rounded bg-white/10" />
                  <div className="h-4 w-2/3 animate-pulse rounded bg-white/10" />
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {!loading && !slice.length ? (
          <p className="mt-8 rounded-2xl bg-white/10 p-6 text-sm">Ainda não há matérias publicadas.</p>
        ) : null}

        <div className="mt-10 space-y-10">
          {feed.map((entry) => {
            if (entry.kind === 'banner') {
              return <NewsInlineBanner key={entry.key} banner={entry.banner} />
            }
            const item = articlesByKey.get(entry.key)
            if (!item) return null
            const paragraphs = splitNewsBody(item.body)
            return (
              <article key={item._id || item.slug} className="overflow-hidden rounded-[28px] bg-white text-[#12307a] shadow-xl">
                <Link to={articlePath(item.slug)} className="block">
                  <WatermarkedImage src={item.featuredImage} alt={item.title} className="aspect-video w-full" imgClassName="h-full w-full object-cover" />
                </Link>
                <div className="p-5 sm:p-8">
                  <div className="flex flex-wrap items-center gap-2 text-xs font-bold uppercase tracking-wide text-[#2a63e8]">
                    <span className="rounded-full bg-[#edf3ff] px-2.5 py-1">{item.category}</span>
                    <span>{formatNewsDate(item.createdAt)}</span>
                    <span>Por {item.author || 'Redação'}</span>
                  </div>
                  <h2 className="mt-3 font-display text-2xl font-black leading-tight sm:text-3xl">
                    <Link to={articlePath(item.slug)}>{item.title}</Link>
                  </h2>
                  <div className="mt-4 space-y-3 text-sm leading-7 text-slate-700 sm:text-base">
                    {paragraphs.length ? paragraphs.map((paragraph, index) => (
                      <p key={`${item._id || item.slug}-p-${index}`}>{paragraph}</p>
                    )) : (
                      <p>{item.excerpt}</p>
                    )}
                  </div>
                  {(item.gallery || []).length > 1 ? (
                    <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
                      {item.gallery?.map((url, index) => (
                        <WatermarkedImage key={`${url}-${index}`} src={url} alt="" className="aspect-video w-full rounded-xl" imgClassName="h-full w-full object-cover" />
                      ))}
                    </div>
                  ) : null}
                  <Link
                    to={articlePath(item.slug)}
                    className="mt-5 inline-flex rounded-full bg-[#0b2ea8] px-5 py-2.5 text-sm font-bold text-white"
                  >
                    Ler matéria completa
                  </Link>
                </div>
              </article>
            )
          })}
        </div>

        {published.length > NEWS_PAGE_SIZE ? (
          <div className="mt-10 flex items-center justify-center gap-3 sm:mt-12">
            <button
              type="button"
              onClick={() => setPage((value) => Math.max(0, value - 1))}
              disabled={currentPage === 0}
              className="inline-flex items-center gap-1 rounded-full bg-white px-4 py-2 text-sm font-bold text-[#0b2ea8] disabled:opacity-40"
            >
              <ChevronLeft size={16} /> Anterior
            </button>
            <span className="text-sm font-bold">{currentPage + 1} / {totalPages}</span>
            <button
              type="button"
              onClick={() => setPage((value) => Math.min(totalPages - 1, value + 1))}
              disabled={currentPage >= totalPages - 1}
              className="inline-flex items-center gap-1 rounded-full bg-white px-4 py-2 text-sm font-bold text-[#0b2ea8] disabled:opacity-40"
            >
              Próxima <ChevronRight size={16} />
            </button>
          </div>
        ) : null}

        <Link to="/" className="mt-8 inline-flex rounded-full border border-white/30 px-5 py-2 text-sm font-bold">
          Voltar ao site
        </Link>
      </main>
      <SiteFooter settings={settings} />
    </div>
  )
}

export default NewsList
